import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import { CONTENT_MARKS } from 'data/layoutcreator/queries/content-marks'
import { TOGGLE_CONTENT_MARK } from 'data/layoutcreator/mutations/content-marks'

export enum ContentMarkType {
  ProfilePage = 'PROFILE_PAGE',
  Collage = 'COLLAGE',
  CustomPage = 'CUSTOM_PAGE',
  Report = 'REPORT',
  Quote = 'QUOTE',
  Ranking = 'RANKING'
}

type ContentMark = {
  groupId: string
  moduleInstanceId: string
  contentType: ContentMarkType
  contentId: string
}

type ContentMarksData = {
  contentMarks: ContentMark[]
}

const useContentMarks = ({
  groupId,
  moduleInstanceId
}: {
  groupId?: string
  moduleInstanceId?: string
}) => {
  const { data, loading } = useQuery<ContentMarksData>(CONTENT_MARKS, {
    skip: !groupId || !moduleInstanceId,
    variables: {
      groupId: groupId as string,
      moduleInstanceId: moduleInstanceId as string
    }
  })

  const [toggleMutation] = useMutation(TOGGLE_CONTENT_MARK, {
    refetchQueries: [
      {
        query: CONTENT_MARKS,
        variables: {
          groupId,
          moduleInstanceId
        }
      }
    ]
  })

  const markedIds = useMemo(() => {
    const set = new Set<string>()
    data?.contentMarks?.forEach(mark => set.add(mark.contentId))
    return set
  }, [data])

  const toggleMark = useCallback(
    (contentId: string, contentType: ContentMarkType) => {
      if (!groupId || !moduleInstanceId) return

      toggleMutation({
        variables: {
          input: {
            groupId,
            moduleInstanceId,
            contentType,
            contentId
          }
        }
      })
    },
    [groupId, moduleInstanceId, toggleMutation]
  )

  return {
    markedIds,
    toggleMark,
    loading
  }
}

export default useContentMarks
